/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef} from 'react'
import {Box, Stack, Flex, Skeleton, useMultiStyleConfig} from '@chakra-ui/react'
import {useOrderDetails} from '../../custom-hooks/use-order-details'
import OrderSummarySection from '../account/my-orders/order-details/order-summary-section'
import OrderBodySection from '../account/my-orders/order-details/order-body-section'
import {OrderProvider} from '../account/my-orders/order-context'

const GuestOrder = () => {
    const {order, isLoading, refetchOrder} = useOrderDetails(true)
    const styles = useMultiStyleConfig('Orders')

    const headingRef = useRef()
    useEffect(() => {
        // Focus the 'Order Details' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    return (
        <OrderProvider>
            <Stack {...styles.guestOrderContainer}>
                <Box layerStyle="cardBordered">
                    {isLoading ? (
                        <>
                            <Stack data-testid="skeleton-stack">
                                <Skeleton h="20px" w="84px" />
                                <Skeleton h="20px" w="112px" />
                                <Skeleton h="20px" w="56px" />
                            </Stack>
                            <Stack data-testid="skeleton-stack">
                                <Skeleton h="20px" w="84px" />
                                <Skeleton h="20px" w="56px" />
                            </Stack>
                            <Stack data-testid="skeleton-stack">
                                <Skeleton h="20px" w="112px" />
                                <Skeleton h="20px" w="84px" />
                                <Skeleton h="20px" w="56px" />
                            </Stack>
                            <Stack data-testid="skeleton-stack">
                                <Skeleton h="20px" w="60px" />
                                <Skeleton h="20px" w="84px" />
                                <Skeleton h="20px" w="56px" />
                            </Stack>
                        </>
                    ) : (
                        typeof order === 'object' && (
                            <OrderSummarySection order={order} refetchOrder={refetchOrder} />
                        )
                    )}
                </Box>

                <Box>
                    {isLoading
                        ? [1, 2, 3].map((i) => (
                              <Box
                                  key={i}
                                  p={[4, 6]}
                                  border="1px solid"
                                  borderColor="gray.100"
                                  borderRadius="base"
                              >
                                  <Flex
                                      width="full"
                                      align="flex-start"
                                      data-testid="skeleton-stack"
                                  >
                                      <Skeleton boxSize={['88px', 36]} mr={4} />

                                      <Stack spacing={2}>
                                          <Skeleton h="20px" w="112px" />
                                          <Skeleton h="20px" w="84px" />
                                          <Skeleton h="20px" w="140px" />
                                      </Stack>
                                  </Flex>
                              </Box>
                          ))
                        : typeof order === 'object' && (
                              <OrderBodySection order={order} refetchOrder={refetchOrder} />
                          )}
                </Box>
            </Stack>
        </OrderProvider>
    )
}

GuestOrder.getTemplateName = () => 'order-detail'

export default GuestOrder
